type EqualsWrapped<T> = T extends (infer R) & {}
  ? {
      [P in keyof R]: R[P];
    }
  : never;

export type Equals<A, B> =
  (<T>() => T extends EqualsWrapped<A> ? 1 : 2) extends <
    T,
  >() => T extends EqualsWrapped<B> ? 1 : 2
    ? true
    : false;

export enum CharCase {
  Upper = 0,
  Lower = 1,
  Digit = 2,
  Unknown = 3,
}

function isCase(char: string): CharCase {
  if (char.length !== 1) {
    throw new Error('expected string with length of 1');
  }

  if (char >= 'A' && char <= 'Z') {
    return CharCase.Upper;
  } else if (char >= 'a' && char <= 'z') {
    return CharCase.Lower;
  } else if (char >= '0' && char <= '9') {
    return CharCase.Digit;
  } else {
    return CharCase.Unknown;
  }
}

/**
 * Converts any **strict** camelCase identifier (e.g.: fooBarBaz) into its
 * upper case equivalent.
 *
 * Example:
 *   'fooBarBaz' -> 'FOO_BAR_BAZ'
 *
 * Careful though! Certain uppercase abbreviations (e.g., fooURL) will return
 * unexpected results. `fooURL` would return `FOO_U_R_L`.
 */
export function camelCaseToUpperCase(input: string): string {
  let output = '';
  for (const char of input) {
    output += isCase(char) === CharCase.Upper ? `_${char}` : char.toUpperCase();
  }

  return output;
}

/**
 * Converts any uppercase identifier (e.g.: FOO_BAR_BAZ) into its camel case
 * equivalent. This function also handles leading and dangling underscores (see below).
 *
 * Examples:
 *   - 'FOO_BAR_BAZ' -> 'fooBarBaz'
 *   - '_FOO_BAR_BAZ' -> '_fooBarBaz'
 *   - 'FOO_BAR_BAZ_' -> 'fooBarBaz_'
 *
 */
export function upperCaseToCamelCase(stringInUpperCase: string): string {
  /* Recursive IIFE to generate base Camel Case string */
  const convertedCamelCaseString = (function innerUpperToCamelCase(
    str: string,
  ): string {
    if (str.length === 0) {
      return '';
    }
    if (str.length === 1 && str.startsWith('_')) {
      /* If we end with an underscore, the result should also end with an underscore. */
      return '_';
    }

    return str.startsWith('_')
      ? /* If underscore, advance to next char */
        str.charAt(1) + innerUpperToCamelCase(str.slice(2))
      : /* Base case is when no underscore (`_`) is present -> convert to lowercase */
        str.charAt(0).toLowerCase() + innerUpperToCamelCase(str.slice(1));
  })(stringInUpperCase);

  const hasLeadingUnderscore = stringInUpperCase.startsWith('_');

  return hasLeadingUnderscore
    ? /** The edge case, when a string starts with an underscore, the result should
      also start with an underscore and have a lowercase next char. */
      `_${
        convertedCamelCaseString.charAt(0).toLowerCase() +
        convertedCamelCaseString.slice(1)
      }`
    : convertedCamelCaseString;
}
